import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { AppTenantConfig } from '@web-config-app/core';
import type { TenantId } from '../../types';
import { useTenantId } from '../use-tenant-id/use-tenant-id';

type TenantConfigMap = Record<TenantId, AppTenantConfig> | undefined;

/**
 * Provides a `selectTenant` callback that switches the active tenant.
 *
 * Switching clears the React Query cache, updates the stored tenant ID
 * (which triggers a new Auth0 token acquisition via LeagueProvider), and
 * navigates to the app root so stale environment keys are not carried over.
 *
 * @param tenantConfigMap - Map of accessible tenant IDs to their configs,
 *   sourced from the Auth0 discovery token's `accessible_tenants` claim.
 */
export const useTenantSelector = (tenantConfigMap: TenantConfigMap) => {
  const { setTenantId } = useTenantId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const selectTenant = useCallback(
    (tenantId: string) => {
      if (!tenantConfigMap || !(tenantId in tenantConfigMap)) {
        // eslint-disable-next-line no-console
        console.error(new Error(`Unknown tenant ID: ${tenantId}`));
        return;
      }

      // Clear all cached queries before switching tenants. This forces
      // hooks like useGetPermissions to start fresh ('pending' → 'success')
      // rather than returning cached 'success' immediately, which avoids
      // a race condition where LeagueProvider's config reset (parent effect)
      // wipes permissionsFilterComplete set by a child effect that won't
      // re-run because its deps haven't changed.
      queryClient.clear();
      // By setting a new tenant ID, we trigger a re-render of LeagueProvider with the new tenant's config,
      // which causes it to fetch a new token for the new tenant and update the auth context with the new tenant-scoped token.
      // This effectively "switches" the tenant for the entire app since all API calls use the token from the auth context.
      setTenantId(tenantId);
      // Navigate to root so the URL doesn't carry over an environmentKey
      // from the previous tenant that may not exist in the new tenant's config.
      // LandingRedirect will route to the correct landing page.
      navigate('/', { replace: true });
    },
    [tenantConfigMap, setTenantId, queryClient, navigate],
  );

  return { selectTenant };
};
